import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BeatLoader from "react-spinners/BeatLoader";
import ClockIcon from "@material-ui/icons/AccessTime";
import CalendarIcon from "@material-ui/icons/Event";
import PersonIcon from "@material-ui/icons/Person";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import { isValidLocation } from "../../util/locationUtil";
import GoogleMap from "../GoogleMap/GoogleMap";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { logVirtualPageView } from "../../util/analytics";
import { sessionStorageManager, SSItem } from "../../util/sessionStorageUtil";
import { formatPhone } from "../../util/phoneUtils";
// "constants/index", not "constants" — see the note in AddGuest/Booking.tsx. The bare
// specifier resolves to Node's ambient `constants` module, not src/constants/.
import { VisitStatus } from "constants/index";
import { getReservationById } from "../../services/reservation.service";
import {
  confirmAppointment,
  createAppointmentWithPendingStatus,
  editAppointment
} from "../../services/appointment.service";

// Steps reused from the booking flow — they read the session-storage payload
// generically and carry no party-size assumptions (plan §5.3).
import DetailsStep from "../AddGuest/SelfReservationSteps/DetailsStep";
import ConfirmStep from "../AddGuest/SelfReservationSteps/ConfirmStep";
import EditStep from "../AddGuest/SelfReservationSteps/EditStep";
import CancellationConfirmStep from "../AddGuest/SelfReservationSteps/CancellationConfirmStep";

import SelectServiceStep from "./SelectServiceStep";
import SelectResourceStep, { ANY_RESOURCE } from "./SelectResourceStep";
import SelectDateTimeStep from "./SelectDateTimeStep";
import LandingPageStep from "./LandingPageStep";
import "./Appointments.scss";

// Appointment booking controller (plan §5.2). Mirrors Booking.tsx's step machine
// without touching it: the first three steps are sequenced by
// `settings.appointments.stepOrder`, and the trailing Details -> Confirm steps are
// identical in both orders.

const STEPS = {
  LANDING: "LANDING",
  SELECT_SERVICE: "SELECT_SERVICE",
  SELECT_RESOURCE: "SELECT_RESOURCE",
  SELECT_DATE_TIME: "SELECT_DATE_TIME",
  DETAILS: "DETAILS",
  CONFIRM: "CONFIRM",
  CONFIRM_CHANGE: "CONFIRM_CHANGE",
  EDIT: "EDIT",
  CANCELLATION_CONFIRM: "CANCELLATION_CONFIRM"
};

// Where a back button is offered. DETAILS is included even though a slot is already
// held server-side there (createAppointmentWithPendingStatus) — there is no client call
// to release that hold, so stepping back leaves it in place until the existing
// pending-removal task expires it, rather than freeing it immediately.
const BACKABLE_STEPS = [STEPS.SELECT_SERVICE, STEPS.SELECT_RESOURCE, STEPS.SELECT_DATE_TIME, STEPS.DETAILS];

function Appointments(props) {
  const navigate = useNavigate();
  const appearance = props.appearance;
  const business = props.business;
  const { display } = useSelector((state: any) => state || { map: true });
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [mapLoading, setMapLoading] = useState(false);

  const buttonStyle = {
    fontFamily: appearance.fontFamily,
    backgroundColor: appearance.highlightBackgroundColor,
    color: appearance.highlightFontColor
  };

  // appointmentCheckInData always sends a fully-resolved `appointments` object — every
  // field defaulted server-side by getAppointmentSettings() — so there is nothing to
  // re-derive here. Defaulting again would just be a second place to keep in sync.
  const { stepOrder, showFirstAvailable, showResourcePhotos, resourceLabelSingular, resourceLabelPlural, landingPage } =
    business?.settings?.appointments || {};

  /**
   * Whether the flow opens on the owner's landing page (plan §17.6).
   *
   * Three conditions, and the content check is not redundant with the toggle: an owner
   * can turn the switch on and save nothing, and an empty first screen with a Book now
   * button under it is worse than no first screen. Widget mode is excluded because the
   * host page already supplies the context a landing page exists to give.
   *
   * A disabled page arrives with `content: ""` anyway — the server withholds the draft
   * (§17.5) — so this is belt-and-braces on that too.
   */
  const showLanding = !!landingPage?.enabled && !!landingPage?.content?.trim() && !display.widgetMode;

  // The business's own word for a bookable resource — "Stylist", "Room", "Lane".
  // Empty when neither the owner nor the account-type preset supplied one, which the
  // steps read as "use noun-free copy".
  const resourceLabel = { singular: resourceLabelSingular || "", plural: resourceLabelPlural || "" };

  const services = business?.services || [];
  const resources = business?.resources || [];

  const SStimerManager = sessionStorageManager(SSItem.TIMER);
  const SSReservationInfoManager = sessionStorageManager(SSItem.RESERVATION_INFO);

  // `search` is the effect dependency, never `query` — a fresh URLSearchParams is
  // built on every render, so depending on the object itself would re-run the URL
  // effect below forever (it sets object state, which never bails out).
  const { pathname, search } = useLocation();
  const query = new URLSearchParams(search);

  /**
   * Where booking itself starts. `stepOrder` decides it normally, but an opening step
   * with nothing in it is a dead end, so a business that has defined only one of the
   * two falls through to whichever list it actually filled in. With both empty there is
   * nothing to fall through to, so the configured step opens and renders its own
   * empty message.
   *
   * Distinct from `firstStep` below: the landing page sits *ahead* of this, and the
   * recovery paths need the booking step rather than the marketing copy.
   */
  const bookingEntryStep = () => {
    if (stepOrder === "resource-first") {
      return !resources.length && services.length ? STEPS.SELECT_SERVICE : STEPS.SELECT_RESOURCE;
    }
    return !services.length && resources.length ? STEPS.SELECT_RESOURCE : STEPS.SELECT_SERVICE;
  };

  /** Where the flow opens for a guest arriving with no deep link. */
  const firstStep = () => (showLanding ? STEPS.LANDING : bookingEntryStep());

  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(firstStep);
  /**
   * Steps already passed through, most recent last — the back button pops it. Kept as a
   * trail rather than recomputed from `stepOrder` because which steps actually render
   * depends on whether the business defined services or resources at all, and on the
   * fall-through in firstStep(); a trail records what the guest really saw.
   * Only the picker steps push onto it, so back can never re-enter the flow behind a
   * slot hold (see BACKABLE_STEPS).
   */
  const [history, setHistory] = useState<string[]>([]);

  const goToStep = (next: string) => {
    setHistory(prev => [...prev, step]);
    setStep(next);
  };

  const restart = () => {
    setSelectedService(null);
    setSelectedResource(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setHistory([]);
    // `bookingEntryStep()`, not `firstStep()`. Every call site here is an error or
    // recovery path where the guest has already opted into booking; bouncing them back
    // to the landing page would bury the error they need to read.
    setStep(bookingEntryStep());
    // Drop ?step and ?id for real — mutating the local URLSearchParams would leave
    // the address bar untouched, and the URL effect below would put the old step back.
    navigate(pathname, { replace: true });
  };

  /**
   * Return to the previous picker and drop the choice that step owns, so it reopens as a
   * fresh question rather than showing an answer the guest has just walked away from.
   */
  const goBack = () => {
    if (!history.length) {
      return;
    }
    const previous = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setError("");
    if (previous === STEPS.SELECT_SERVICE) {
      setSelectedService(null);
    }
    if (previous === STEPS.SELECT_RESOURCE) {
      setSelectedResource(null);
    }
    if (step === STEPS.DETAILS) {
      // Drop the pending-reservation reference so a reload doesn't snap the guest
      // straight back into Details — the hold itself isn't released here, it just
      // expires on its own (see BACKABLE_STEPS above).
      SSReservationInfoManager.remove();
      SStimerManager.remove();
      navigate(pathname, { replace: true });
    }
    setStep(previous);
  };

  // Services this resource performs, for the resource-first order. Resolved by id for
  // the same reason as guestResourceName below: a `selectedResource` rebuilt from the
  // hold or from session storage carries only `{ id, name }`, with no `serviceIds`.
  const servicesForResource = (resource: any) => {
    if (!resource?.id || resource.id === ANY_RESOURCE) {
      return services;
    }
    const serviceIds = resources.find((r: any) => r.id === resource.id)?.serviceIds || resource.serviceIds || [];
    return services.filter((s: any) => serviceIds.includes(s.id));
  };

  // Resources performing this service, for the service-first order.
  const resourcesForService = (service: any) =>
    service ? resources.filter((r: any) => (r.serviceIds || []).includes(service.id)) : resources;

  /**
   * What the guest should see called this resource — `displayName` when the owner set
   * one, matching the picker they chose from. Resolved by id rather than read off
   * `selectedResource`, because the hold, the session-storage payload, and the edit
   * fetch all carry the *stored* `resourceName`, which is the internal admin label.
   * Falls back to whatever name we were handed, for a resource since deleted.
   */
  const guestResourceName = (resource: any) => {
    if (!resource?.id || resource.id === ANY_RESOURCE) {
      return null;
    }
    const match = resources.find((r: any) => r.id === resource.id);
    return match?.displayName || match?.name || resource.name || null;
  };

  const onServiceSelected = (service: any) => {
    setSelectedService(service);
    if (stepOrder === "resource-first") {
      goToStep(STEPS.SELECT_DATE_TIME);
      return;
    }
    // Nobody to choose between: the business books this service against itself.
    if (!resources.length) {
      goToStep(STEPS.SELECT_DATE_TIME);
      return;
    }
    goToStep(STEPS.SELECT_RESOURCE);
  };

  const onResourceSelected = (resource: any) => {
    setSelectedResource(resource);
    // Nothing to choose between: the resource is booked on its own default duration.
    if (stepOrder === "resource-first" && services.length) {
      goToStep(STEPS.SELECT_SERVICE);
      return;
    }
    goToStep(STEPS.SELECT_DATE_TIME);
  };

  // Rescheduling an existing appointment rather than booking a new one: EditStep's
  // "Change" button navigates to ?id=<id> with no step, which lands on the date/time
  // step with the service and resource reloaded from the appointment itself.
  const reschedulingId = () => (query.has("step") ? null : query.get("id"));

  const onReschedule = async (slot: any) => {
    const reservationId = reschedulingId();
    const res = await editAppointment({
      reservationId,
      businessId: business.id,
      serviceId: selectedService?.id,
      resourceId: selectedResource?.id,
      date: slot.date,
      time: slot.time
    });

    if (!res?.success) {
      throw new Error(res?.error || "Something went wrong");
    }

    setSelectedDate(slot.date);
    setSelectedTime(slot.time);
    setContentLoading(false);
    logVirtualPageView("Waitly - Appointment Change Confirmation");
    navigate(`?step=confirm-edit&id=${reservationId}`);
  };

  const onSlotSelected = async (slot: any) => {
    setContentLoading(true);
    setError("");
    try {
      if (reschedulingId()) {
        await onReschedule(slot);
        return;
      }

      const res = await createAppointmentWithPendingStatus({
        businessId: business.id,
        serviceId: selectedService?.id,
        // The server re-picks among whoever is free when the guest chose First Available.
        resourceId: selectedResource?.id,
        date: slot.date,
        time: slot.time
      });

      if (!res?.success || !res.data?.id) {
        throw new Error(res?.error || "Something went wrong");
      }

      setSelectedDate(slot.date);
      setSelectedTime(slot.time);
      // The matched resource comes back from the hold, which is authoritative when
      // the guest chose First Available — the slot list's suggestion was only a preview.
      setSelectedResource({
        id: res.data.resourceId,
        name: res.data.resourceName
      });

      SSReservationInfoManager.set({
        id: res.data.id,
        date: slot.date,
        time: slot.time,
        serviceId: selectedService?.id,
        serviceName: selectedService?.name,
        resourceId: res.data.resourceId,
        resourceName: res.data.resourceName
      });
      SStimerManager.set(new Date());

      setContentLoading(false);
      // `goToStep`, not `setStep` — records the date/time step in `history` so the
      // Details step's back button returns to it instead of finding nothing there.
      goToStep(STEPS.DETAILS);
      navigate("?step=details");
    } catch (e) {
      setError((e as Error)?.message || "Something went wrong");
      setContentLoading(false);
    }
  };

  const onSubmitDetails = async values => {
    setContentLoading(true);
    try {
      const stored = SSReservationInfoManager.get() || {};
      const res = await confirmAppointment({
        ...values,
        ...stored,
        reservationId: stored.id,
        businessId: business.id,
        phone: formatPhone(values.phone)
      });

      if (!res?.success || !res.data?.reservationId) {
        throw new Error(res?.error || "Something went wrong");
      }
      setContentLoading(false);
      logVirtualPageView("Waitly - Appointment Confirmation");
      navigate(`?step=confirm&id=${res.data.reservationId || ""}`);
    } catch (e) {
      setError((e as Error)?.message || "Something went wrong");
      setContentLoading(false);
      restart();
    }
  };

  const appointmentIsValidForEditing = appointment =>
    [VisitStatus.reserved, VisitStatus.waiting, VisitStatus.notified].includes(appointment.status) &&
    appointment.date.getTime() > new Date().getTime();

  const fetchAppointment = async () => {
    if (!query.has("id")) {
      return;
    }
    const res = await getReservationById(query.get("id"));
    if (!res?.success) {
      restart();
      setError(res?.error || res?.message || "Something went wrong");
      return;
    }
    const date = DateTime.fromFormat(res.data?.date, "yyyy-MM-dd");
    if (
      Reflect.has(res.data, "status") &&
      appointmentIsValidForEditing({
        ...res.data,
        date: new Date(DateTime.fromSeconds(res.data.reservationTime._seconds).toISO() as string)
      })
    ) {
      setSelectedDate(date.isValid ? (date.toISODate() as string) : null);
      setSelectedTime(res.data?.time || null);
      setSelectedService({ id: res.data?.serviceId, name: res.data?.serviceName });
      setSelectedResource({ id: res.data?.resourceId, name: res.data?.resourceName });
      return;
    }
    restart();
  };

  // Lets the page shell (AddGuest) hide its own "Powered by Waitly" footer while the
  // owner's landing page is showing — that screen is meant to read as the business's
  // own page, not a Waitly-branded step.
  useEffect(() => {
    props.onLandingStepChange?.(step === STEPS.LANDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step === STEPS.CONFIRM || step === STEPS.CONFIRM_CHANGE) {
      SStimerManager.remove();
      SSReservationInfoManager.remove();
      fetchAppointment();
      return;
    }
    // The date/time step only needs a fetch when rescheduling — that is where the
    // service and resource of the existing appointment come from.
    if (step === STEPS.EDIT || (step === STEPS.SELECT_DATE_TIME && reschedulingId())) {
      fetchAppointment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    const requested = query.get("step");
    const id = query.get("id");

    if (requested === "details" && SSReservationInfoManager.get()) {
      const stored = SSReservationInfoManager.get();
      setSelectedDate(stored.date);
      setSelectedTime(stored.time);
      // Falsy id -> null, not `{ id: undefined, name: undefined }`. A business with no
      // services (or none linked to the chosen resource) never sets `serviceId` here,
      // and a truthy-but-empty object reads downstream as "a service is selected" —
      // `resourcesForService`/`servicesForResource` then filter against an id nothing
      // matches, turning a real picker into a false "nothing available" empty state
      // (surfaced by stepping back from Details into the resource/service picker).
      setSelectedService(stored.serviceId ? { id: stored.serviceId, name: stored.serviceName } : null);
      setSelectedResource(stored.resourceId ? { id: stored.resourceId, name: stored.resourceName } : null);
      setStep(STEPS.DETAILS);
    } else if (requested === "confirm" && id) {
      setStep(STEPS.CONFIRM);
    } else if (requested === "confirm-edit" && id) {
      setStep(STEPS.CONFIRM_CHANGE);
    } else if (requested === "edit" && id) {
      setStep(STEPS.EDIT);
    } else if (requested === "cancellation-confirm") {
      setStep(STEPS.CANCELLATION_CONFIRM);
    } else if (!requested && id) {
      setStep(STEPS.SELECT_DATE_TIME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // "Haircut · Nathan", or just whichever half the business actually defined. Joined
  // with a separator rather than "with": the preposition changes with the noun ("with
  // Nathan", "in Room 3", "on Lane 4") and no label setting can carry that.
  const bookedResourceName = guestResourceName(selectedResource);
  const bookedSummary = [selectedService?.name, bookedResourceName].filter(Boolean).join(" · ");

  const APPOINTMENT_DETAILS = selectedDate && selectedTime && (
    <div style={{ marginBottom: 20 }}>
      {!!bookedSummary && (
        <div className="self-reservations__title reservation-details small">
          <PersonIcon className="self-reservations__icon" style={{ fill: buttonStyle.backgroundColor }} />{" "}
          {bookedSummary}
        </div>
      )}
      <div className="self-reservations__title reservation-details small">
        <CalendarIcon style={{ fill: buttonStyle.backgroundColor }} />{" "}
        {DateTime.fromISO("" + selectedDate).toFormat("MM/dd/yyyy")}
      </div>
      <div className="self-reservations__title reservation-details small">
        <ClockIcon style={{ fill: buttonStyle.backgroundColor }} />{" "}
        {DateTime.fromFormat(selectedTime, "HH:mm").toFormat("hh:mm a")}
      </div>
    </div>
  );

  // Whether the right-hand address column renders at all. `isValidLocation` is the same
  // check GoogleMap makes internally — mirrored here so the layout knows the column is
  // coming, since a business with no address produces the same empty half as hideMap.
  const showMap =
    step !== STEPS.LANDING && !display.widgetMode && !business?.settings?.hideMap && isValidLocation(business);

  const pages = () => {
    switch (step) {
      case STEPS.LANDING:
        return (
          <LandingPageStep
            content={landingPage?.content}
            buttonStyle={buttonStyle}
            background={appearance.addGuestBackground}
            // Enters whichever step `stepOrder` selects, so the landing page is purely
            // additive — nothing about the existing sequencing changes.
            onStart={() => goToStep(bookingEntryStep())}
          />
        );
      case STEPS.SELECT_SERVICE:
        return (
          <SelectServiceStep
            services={stepOrder === "resource-first" ? servicesForResource(selectedResource) : services}
            onSelect={onServiceSelected}
            buttonStyle={buttonStyle}
            loading={false}
          />
        );
      case STEPS.SELECT_RESOURCE:
        return (
          <SelectResourceStep
            resources={stepOrder === "resource-first" ? resources : resourcesForService(selectedService)}
            onSelect={onResourceSelected}
            buttonStyle={buttonStyle}
            loading={false}
            // The pseudo-option only makes sense in service-first order, where the
            // server can pick among everyone performing the chosen service.
            showFirstAvailable={stepOrder === "service-first" && showFirstAvailable}
            showPhotos={showResourcePhotos}
            label={resourceLabel}
          />
        );
      case STEPS.SELECT_DATE_TIME:
        // A slot query needs a service or a provider — a business that has defined only
        // one of the two books with just that one. Neither means there is nothing at
        // all to book.
        if (!selectedService?.id && !selectedResource?.id) {
          // Rescheduling: waiting on fetchAppointment to say what the existing
          // appointment is for.
          if (reschedulingId()) {
            return (
              <div className="appointments__loading">
                <BeatLoader margin={2.5} size={15} color={buttonStyle?.backgroundColor} />
              </div>
            );
          }
          return (
            <div className="appointments__step">
              <div className="self-reservations__title">Select a date and time</div>
              <div className="appointments__empty">There is nothing available to book right now.</div>
            </div>
          );
        }
        return (
          <SelectDateTimeStep
            business={business}
            serviceId={selectedService?.id}
            serviceName={selectedService?.name}
            resourceId={selectedResource?.id}
            resourceName={guestResourceName(selectedResource)}
            onSelect={onSlotSelected}
            buttonStyle={buttonStyle}
            onError={setError}
          />
        );
      case STEPS.DETAILS:
        return (
          <DetailsStep
            buttonStyle={buttonStyle}
            selectedLocation={business}
            isMobile={isMobile}
            onSubmit={d => onSubmitDetails(d)}
            loading={contentLoading}
          />
        );
      case STEPS.CONFIRM:
        return <ConfirmStep reservationDetails={APPOINTMENT_DETAILS} appearance={appearance} />;
      case STEPS.CONFIRM_CHANGE:
        return <ConfirmStep reservationDetails={APPOINTMENT_DETAILS} isChangeConfirm appearance={appearance} />;
      case STEPS.EDIT:
        return (
          <EditStep reservationId={query.get("id")} reservationDetails={APPOINTMENT_DETAILS} appearance={appearance} />
        );
      case STEPS.CANCELLATION_CONFIRM:
        return <CancellationConfirmStep />;
      default:
        return <>Something went wrong</>;
    }
  };

  return (
    <div style={{ background: appearance.addGuestBackground }}>
      <div
        className={`self-reservations appointments ${showMap ? "" : "no-map"} ${
          step === STEPS.CONFIRM ? "confirm-page" : ""
        }`}
      >
        {business && (
          <>
            <div className="appointments__content">
              {!!history.length && BACKABLE_STEPS.includes(step) && (
                <button type="button" className="appointments__back" onClick={goBack} aria-label="Go back">
                  <ArrowBackIcon className="appointments__back-icon" /> Back
                </button>
              )}
              {!!error && <div className="appointments__error">{error}</div>}
              {pages()}
            </div>
            {showMap && (
              <GoogleMap
                reservationDetails={!isMobile && step === STEPS.DETAILS && APPOINTMENT_DETAILS}
                withLeftSeperator
                loading={mapLoading}
                setLoading={setMapLoading}
                isLocationValid={isValidLocation(business)}
                location={business}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Appointments;
